import type { Board, GeometryElement, Point } from 'jsxgraph';
import type { ElementFactory, JsxgraphThemeService, ParameterizedElement } from '@examdojo/core/widget';
import type { ElementDef, ElementDisplayConfig, FeatureType, HandleBehavior } from '../graphpad.model';
import type { FeatureTheme } from './features/feature-style.model';
import type { HandleConfigs } from './functions/function.model';

// =============================================================================
// ELEMENT CONTEXT - Passed to all creators
// =============================================================================

export interface ElementContext {
  /** The JSXGraph board instance */
  board: Board;
  /** All created elements, for dependency resolution */
  elements: Map<string, CreatedElement>;
  /** Get current value of a parameter (may be from slider or element params) */
  getParam: (path: string) => number;
  /** Theme service for colors */
  themeService: JsxgraphThemeService;
  /** Element factory for creating styled elements */
  elementFactory: ElementFactory;
  /** Feature theme with default styles for feature types */
  featureTheme: FeatureTheme;
  /** Container HTML element */
  container: HTMLElement;
  /**
   * Callback to notify when interactive elements change params via drag.
   * Feature creators should use this to report glider/boundary position changes.
   */
  onParamChange?: (elementId: string, params: Record<string, number>) => void;
}

// =============================================================================
// CREATED ELEMENT - Result of creation
// =============================================================================

export interface CreatedElement {
  /** Unique identifier for this element */
  id: string;
  /** The element type */
  type: string;
  /** The JSXGraph objects created */
  objects: GeometryElement[];
  /** For functions: the evaluation function */
  evaluate?: (x: number) => number;
  /** For functions: the handles that control this function */
  handles?: Map<HandleBehavior, Point>;
  /** For functions: get current parameter values */
  getParams?: () => Record<string, number>;
  /** For functions: set parameter values (updates handles and internal state) */
  setParams?: (params: Partial<Record<string, number>>) => void;
  /** For functions: get derivative function */
  getDerivative?: (order?: number) => (x: number) => number;
  /** Cleanup function */
  destroy: () => void;
}

// =============================================================================
// FUNCTION ELEMENT - Extended interface for function elements
// =============================================================================

export interface FunctionElement extends CreatedElement {
  /** Evaluate the function at x */
  evaluate: (x: number) => number;
  /** Get the derivative function (computed symbolically or numerically) */
  getDerivative: (order?: number) => (x: number) => number;
  /**
   * Get current parameter values.
   * Reads from the underlying JsxParamStore (source of truth).
   */
  getParams: () => Record<string, number>;
  /**
   * Set parameter values.
   *
   * IMPORTANT: Implementations MUST:
   * 1. Update the underlying JSXGraph sliders (via JsxParamStore.setParams)
   * 2. Sync handle positions to match the new param values (via onSync callback)
   * 3. Call board.update() to refresh the display
   *
   * This ensures UI slider changes propagate to the graph correctly.
   * The handle-to-param mapping is function-specific (e.g., quadratic: h→x, k→y).
   *
   * Use JsxParamStore from @examdojo/core/widget with createTranslateSync helper
   * to implement this correctly.
   */
  setParams: (params: Partial<Record<string, number>>) => void;
  /**
   * Handle configurations keyed by behavior.
   * Each behavior maps to its specific configuration (X/Y param mapping).
   * Used by HandleCreator to know which params to update when handles are dragged.
   */
  handleConfigs: HandleConfigs;
  /**
   * The handles controlling this function (for drag interaction).
   * Handles are created with static coordinates and synced via setParams().
   * When exposed by HandleCreator, they become draggable and update params on drag.
   */
  handles: Map<HandleBehavior, Point>;
  /** Available features for this function type */
  availableFeatures: readonly FeatureType[];
}

// =============================================================================
// ELEMENT CREATOR INTERFACE
// =============================================================================

export interface ElementCreator<TDef extends ElementDef = ElementDef> {
  /** The element type(s) this creator handles */
  readonly types: string[];

  /** Create the element on the board */
  create(id: string, def: TDef, display: ElementDisplayConfig | undefined, context: ElementContext): CreatedElement;
}

// =============================================================================
// EVALUABLE ELEMENT - Any element that can be evaluated as f(x)
// =============================================================================

/**
 * Interface for elements that can be evaluated as a function f(x).
 * This includes both full FunctionElements and derivative features.
 */
export interface EvaluableElement extends CreatedElement {
  /** Evaluate the function at x */
  evaluate: (x: number) => number;
  /** Get the derivative function (optional for some evaluables) */
  getDerivative?: (order?: number) => (x: number) => number;
}

// =============================================================================
// TYPE GUARDS
// =============================================================================

export function isFunctionElement(element: CreatedElement): element is FunctionElement {
  return (
    element.evaluate !== undefined &&
    element.getDerivative !== undefined &&
    element.getParams !== undefined &&
    element.handles !== undefined &&
    (element as FunctionElement).handleConfigs !== undefined
  );
}

/**
 * Type guard for elements that can be evaluated as f(x).
 * Includes both FunctionElements and derivative features.
 */
export function isEvaluableElement(element: CreatedElement): element is EvaluableElement {
  return element.evaluate !== undefined;
}

/**
 * Type guard for elements that support parameterized updates.
 * Any element with getParams/setParams can be controlled via UPDATE_PARAM action.
 * This includes function elements, feature elements (tangent, secant), and any
 * other element with controllable parameters.
 */
export function isParameterizedElement(element: CreatedElement): element is CreatedElement & ParameterizedElement {
  return typeof element.getParams === 'function' && typeof element.setParams === 'function';
}
